import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  ChartBar,
  ChatText,
  CloudArrowDown,
  DotsSixVertical,
  Exam,
  FileText,
  Flask,
  GraduationCap,
  Monitor,
  NotePencil,
  Pencil,
  Plus,
  PuzzlePiece,
  Question as QuizIcon,
  ShieldWarning,
  Sparkle,
  Star,
  Target,
  Trash,
  VideoCamera,
  type Icon,
} from '@phosphor-icons/react';
import { MATERIAL_SLOTS, MATERIAL_TITLES, MATERIAL_TYPES, SUPPLEMENT_TYPES } from '@cvs/shared';
import type { Chapter, Course, InterviewQuestion, Lab, Lesson, LessonType, ModuleStatus, Section } from '@cvs/shared';
import { api, type ContentRow } from '../lib/api';
import { HtmlUploadField } from '../components/HtmlUploadField';
import { useAsync } from '../lib/useAsync';
import { formatDuration } from '../lib/format';
import { QuizManager } from '../components/QuizManager';
import { StudyManager } from '../components/StudyManager';
import {
  Button,
  Card,
  ContentStatusPill,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Pill,
  cx,
} from '../components/ui';

const STATUSES: ModuleStatus[] = ['planned', 'recording', 'published'];

const MATERIAL_ICON: Record<LessonType, Icon> = {
  recording: VideoCamera,
  slides: Monitor,
  transcript: FileText,
  summary: ChatText,
  notes: NotePencil,
  infographic: ChartBar,
  incident: ShieldWarning,
};

const MATERIAL_HINT: Record<LessonType, string> = {
  recording: 'Paste the SharePoint “Embed” code (or a YouTube/Vimeo link), or upload a video — plays inline in the lesson stage',
  slides: 'PDF preferred (views inline with slide arrows); PPTX ≤10 MB works too',
  transcript: 'Upload a file or paste the text — learners read it alongside the recording',
  summary: 'Short recap of the class — write it or upload a file',
  notes: 'Study notes for revision — write them, or draft from the transcript. Renders formatted in the class',
  infographic: 'Upload an infographic (PNG/JPG image or PDF) — shown inline as extra study material',
  incident: 'Real-world incident case study — write it or upload a PDF; shown as an “Incident report” tab in the class',
};

/** Accept a full <iframe …src="…"> embed snippet or a bare URL; return the URL. */
function extractEmbedSrc(value: string): string {
  const m = value.match(/<iframe[^>]*\ssrc=["']([^"']+)["']/i);
  return (m ? m[1] : value).trim();
}

/** Seconds → "m:ss" (or "h:mm:ss" past an hour). */
function fmtTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** "h:mm:ss" / "m:ss" / bare seconds → seconds. Returns NaN if unparseable. */
function parseTime(value: string): number {
  const parts = value.trim().split(':').map((p) => Number(p));
  if (parts.some((n) => !Number.isFinite(n))) return NaN;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

type SectionModal = { section?: Section } | null;
type ClassModal = { sectionId: string | null; row?: ContentRow } | null;
type MaterialModal = { classId: string; type: LessonType; existing?: Lesson } | null;
type LabModal =
  | { kind: 'lab' | 'exercise'; moduleId: string; lab?: Lab }
  | { kind: 'project'; sectionId: string; lab?: Lab }
  | null;

// ── Course picker — choose a course before editing its curriculum ────────────
function CoursePicker({
  courses,
  sections,
  onPick,
}: {
  courses: Course[];
  sections: Section[];
  onPick: (id: string) => void;
}) {
  const countFor = (id: string | null) => sections.filter((s) => (s.courseId ?? null) === id).length;
  const unfiledCount = countFor(null);
  return (
    <div>
      <div className="mb-5">
        <div className="label-mono">Content</div>
        <h2 className="text-xl font-bold">Curriculum</h2>
        <p className="mt-1 text-sm text-slate-500">
          Pick a course to build its sections and classes. Each course's content stays separate — no mixing.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {courses.map((c) => {
          const n = countFor(c.id);
          return (
            <button
              key={c.id}
              onClick={() => onPick(c.id)}
              className="rounded-2xl border border-slate-200 bg-white p-5 text-left transition hover:border-brand-teal hover:shadow-md"
            >
              <div className="flex items-center gap-2">
                <GraduationCap size={22} weight="bold" className="text-brand-teal" />
                <span className="font-semibold text-brand-navy">{c.title}</span>
              </div>
              <div className="mt-2 text-sm text-slate-500">
                {n} section{n === 1 ? '' : 's'}
                {c.status === 'archived' && ' · archived'}
              </div>
            </button>
          );
        })}
        {unfiledCount > 0 && (
          <button
            onClick={() => onPick('unfiled')}
            className="rounded-2xl border border-dashed border-amber-300 bg-amber-50/40 p-5 text-left transition hover:border-amber-400"
          >
            <div className="font-semibold text-amber-800">Unfiled sections</div>
            <div className="mt-2 text-sm text-amber-700/80">
              {unfiledCount} section{unfiledCount === 1 ? '' : 's'} not in any course — file them into one
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

export function StudioContent() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const content = useAsync(() => api.admin.content(), []);
  const coursesQ = useAsync(() => api.admin.courses(), []);
  const [openClass, setOpenClass] = useState<Record<string, boolean>>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [sectionModal, setSectionModal] = useState<SectionModal>(null);
  const [classModal, setClassModal] = useState<ClassModal>(null);
  const [materialModal, setMaterialModal] = useState<MaterialModal>(null);
  const [labModal, setLabModal] = useState<LabModal>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Optimistic ordering for drag-and-drop.
  const [localSections, setLocalSections] = useState<Section[]>([]);
  const [localModules, setLocalModules] = useState<ContentRow[]>([]);
  const dragSection = useRef<string | null>(null);
  const dragClass = useRef<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  useEffect(() => {
    if (content.data) {
      setLocalSections(content.data.sections);
      setLocalModules(content.data.modules);
    }
  }, [content.data]);

  if (content.loading || coursesQ.loading) return <LoadingState />;
  if (content.error || !content.data)
    return <ErrorState message={content.error ?? 'Failed to load'} onRetry={content.reload} />;

  const sections = localSections.length > 0 ? localSections : content.data.sections;
  const modules = localModules.length > 0 ? localModules : content.data.modules;
  const labs = content.data.labs;
  const courses = coursesQ.data ?? [];
  const sectionIds = new Set(sections.map((s) => s.id));
  const unfiled = modules.filter((m) => !m.sectionId || !sectionIds.has(m.sectionId));

  // The course being edited comes from the URL slug (each course has its own URL);
  // 'unfiled' is the bucket of sections not yet in any course; no slug = the picker.
  const goToPicked = (id: string) => {
    if (id === 'unfiled') return navigate('/studio/content/unfiled');
    const slug = courses.find((c) => c.id === id)?.slug;
    navigate(slug ? `/studio/content/${slug}` : '/studio/content');
  };
  const picked = !courseSlug
    ? null
    : courseSlug === 'unfiled'
      ? 'unfiled'
      : courses.find((c) => c.slug === courseSlug)?.id ?? null;

  // Course-first: choose a course before editing its curriculum.
  if (picked === null) {
    return <CoursePicker courses={courses} sections={sections} onPick={goToPicked} />;
  }
  const pickedCourse = picked !== 'unfiled' ? courses.find((c) => c.id === picked) ?? null : null;
  const visibleSections =
    picked === 'unfiled' ? sections.filter((s) => !s.courseId) : sections.filter((s) => s.courseId === picked);

  async function run(fn: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      content.reload();
      setRefreshKey((n) => n + 1);
      after?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  function dropSection(targetId: string) {
    const from = dragSection.current;
    dragSection.current = null;
    setDragOver(null);
    if (!from || from === targetId) return;
    const list = [...sections];
    const fromIdx = list.findIndex((s) => s.id === from);
    const toIdx = list.findIndex((s) => s.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);
    setLocalSections(list);
    void run(() => api.admin.reorderSections(list.map((s) => s.id)));
  }

  function dropClass(targetId: string) {
    const from = dragClass.current;
    dragClass.current = null;
    setDragOver(null);
    if (!from || from === targetId) return;
    const list = [...modules];
    const fromIdx = list.findIndex((m) => m.id === from);
    const toIdx = list.findIndex((m) => m.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = list.splice(fromIdx, 1);
    const movedInto = list[toIdx]?.sectionId ?? moved.sectionId;
    list.splice(toIdx, 0, { ...moved, sectionId: movedInto });
    setLocalModules(list);
    void run(async () => {
      // Dropping onto a class in another section also refiles the dragged class.
      if (movedInto !== moved.sectionId) await api.admin.updateModule(moved.id, { sectionId: movedInto });
      await api.admin.reorderModules(list.map((m) => m.id));
    });
  }

  // Dropping a class anywhere on a section (incl. an empty one) files it there,
  // appended after the section's existing classes.
  function dropClassOnSection(sectionId: string) {
    const from = dragClass.current;
    dragClass.current = null;
    setDragOver(null);
    if (!from) return;
    const list = [...modules];
    const fromIdx = list.findIndex((m) => m.id === from);
    if (fromIdx < 0) return;
    const [moved] = list.splice(fromIdx, 1);
    let insertAt = list.length;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].sectionId === sectionId) {
        insertAt = i + 1;
        break;
      }
    }
    list.splice(insertAt, 0, { ...moved, sectionId });
    setLocalModules(list);
    void run(async () => {
      if (moved.sectionId !== sectionId) await api.admin.updateModule(moved.id, { sectionId });
      await api.admin.reorderModules(list.map((m) => m.id));
    });
  }

  const renderClass = (m: ContentRow, label: string) => (
    <div
      key={m.id}
      draggable
      onDragStart={() => {
        dragClass.current = m.id;
      }}
      onDragOver={(e) => {
        if (!dragClass.current) return;
        e.preventDefault();
        e.stopPropagation();
        if (dragOver !== m.id) setDragOver(m.id);
      }}
      onDragEnd={() => {
        dragClass.current = null;
        setDragOver(null);
      }}
      onDrop={(e) => {
        if (!dragClass.current) return; // section drops bubble to the container
        e.preventDefault();
        e.stopPropagation();
        dropClass(m.id);
      }}
      className={cx('rounded-2xl transition', dragOver === m.id && dragClass.current && dragClass.current !== m.id && 'ring-2 ring-brand-teal')}
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4">
          <span className="cursor-grab text-slate-300 transition hover:text-slate-500 active:cursor-grabbing" title="Drag to reorder" aria-hidden="true">
            <DotsSixVertical size={18} weight="bold" />
          </span>
          <button
            className="btn-ghost p-1"
            aria-label={openClass[m.id] ? 'Collapse class' : 'Expand class'}
            onClick={() => setOpenClass((s) => ({ ...s, [m.id]: !s[m.id] }))}
          >
            {openClass[m.id] ? <CaretDown size={18} /> : <CaretRight size={18} />}
          </button>
          <span className="flex h-8 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 px-2 text-sm font-bold text-brand-teal">
            {label}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-brand-navy">{m.title}</span>
              <ContentStatusPill status={m.status} />
              <Pill tone={m.recorded >= m.total ? 'green' : 'slate'}>
                Materials {m.recorded}/{m.total}
              </Pill>
            </div>
            {m.topics.length > 0 && (
              <div className="mt-1 truncate text-xs text-slate-500">{m.topics.join(', ')}</div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button variant="secondary" icon={Pencil} onClick={() => setClassModal({ sectionId: m.sectionId, row: m })}>
              Edit
            </Button>
            <button
              className="btn-ghost p-2 text-rose-600"
              aria-label="Delete class"
              onClick={() => {
                if (confirm(`Delete "${m.title}" with its materials and quiz?`)) void run(() => api.admin.deleteModule(m.id));
              }}
            >
              <Trash size={18} />
            </button>
          </div>
        </div>

        {openClass[m.id] && (
          <ClassTabs
            row={m}
            refreshKey={refreshKey}
            onEditMaterial={(type, existing) => setMaterialModal({ classId: m.id, type, existing })}
            onRemoveMaterial={(type) => {
              if (confirm(`Remove the ${MATERIAL_TITLES[type].toLowerCase()} from this class?`))
                void run(() => api.admin.deleteMaterial(m.id, type));
            }}
            onEditLab={(kind, lab) => setLabModal({ kind, moduleId: m.id, lab })}
            onDeleteLab={(lab) => {
              if (confirm(`Delete lab "${lab.title}" and its submissions?`)) void run(() => api.admin.deleteLab(lab.id));
            }}
          />
        )}
      </Card>
    </div>
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            className="label-mono flex items-center gap-1 text-brand-teal hover:underline"
            onClick={() => navigate('/studio/content')}
          >
            <CaretLeft size={12} weight="bold" /> All courses
          </button>
          <h2 className="text-xl font-bold">
            {picked === 'unfiled' ? 'Unfiled sections' : pickedCourse?.title ?? 'Course'}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Sections are big topics; each class holds its materials, lab and knowledge check. New sections you add here
            go into {picked === 'unfiled' ? 'no course (unfiled)' : 'this course'}. Drag{' '}
            <DotsSixVertical size={14} className="inline align-text-bottom" /> to reorder.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setSectionModal({})}>
          Add section
        </Button>
      </div>

      {error && <p className="mb-4 text-sm font-medium text-rose-600">{error}</p>}

      {visibleSections.length === 0 ? (
        <EmptyState title="No sections yet" description="Add the first section (big topic) to this course." />
      ) : (
        <div className="space-y-4">
          {visibleSections.map((sec, si) => {
            const classes = modules.filter((m) => m.sectionId === sec.id);
            const project = labs.find((l) => l.kind === 'project' && l.sectionId === sec.id) ?? null;
            const materialsAdded = classes.reduce((sum, c) => sum + c.recorded, 0);
            const materialsTotal = classes.reduce((sum, c) => sum + c.total, 0);
            const collapsed = collapsedSections[sec.id];
            return (
              <div
                key={sec.id}
                className={cx(
                  'overflow-hidden rounded-2xl border border-slate-200 bg-white/60 transition',
                  dragOver === sec.id &&
                    ((dragSection.current && dragSection.current !== sec.id) || dragClass.current) &&
                    'ring-2 ring-brand-teal',
                )}
                onDragOver={(e) => {
                  if (!dragSection.current && !dragClass.current) return;
                  e.preventDefault();
                  if (dragOver !== sec.id) setDragOver(sec.id);
                }}
                onDrop={(e) => {
                  if (dragSection.current) {
                    e.preventDefault();
                    dropSection(sec.id);
                  } else if (dragClass.current) {
                    e.preventDefault();
                    dropClassOnSection(sec.id);
                  }
                }}
              >
                <div
                  className="flex flex-wrap items-center gap-3 bg-brand-navy px-4 py-3"
                  draggable
                  onDragStart={() => {
                    dragSection.current = sec.id;
                  }}
                  onDragEnd={() => {
                    dragSection.current = null;
                    setDragOver(null);
                  }}
                >
                  <span className="cursor-grab text-slate-500 hover:text-slate-300 active:cursor-grabbing" title="Drag to reorder section" aria-hidden="true">
                    <DotsSixVertical size={16} weight="bold" />
                  </span>
                  <span className="rounded-full border border-brand-teal-bright/40 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-brand-teal-bright">
                    Section {si + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{sec.title}</span>
                  <span className="hidden gap-3 text-xs text-slate-400 sm:flex">
                    <span>{classes.length} classes</span>
                    <span>{project ? '1 project' : 'no project'}</span>
                    <span>
                      materials {materialsAdded}/{materialsTotal}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    <button
                      className="rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-white/20"
                      onClick={() => setClassModal({ sectionId: sec.id })}
                    >
                      ＋ Add class
                    </button>
                    <button
                      className="rounded-lg bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-white/20"
                      onClick={() => setSectionModal({ section: sec })}
                    >
                      Edit
                    </button>
                    <button
                      className="rounded-lg bg-white/10 px-2 py-1.5 text-xs text-rose-300 transition hover:bg-white/20"
                      aria-label="Delete section"
                      onClick={() => {
                        if (
                          confirm(
                            `Delete section "${sec.title}"? Its classes are kept (moved to Unfiled); its project is deleted.`,
                          )
                        )
                          void run(() => api.admin.deleteSection(sec.id));
                      }}
                    >
                      <Trash size={14} />
                    </button>
                    <button
                      className="rounded-lg bg-white/10 px-2 py-1.5 text-xs text-slate-200 transition hover:bg-white/20"
                      aria-label={collapsed ? 'Expand section' : 'Collapse section'}
                      onClick={() => setCollapsedSections((s) => ({ ...s, [sec.id]: !s[sec.id] }))}
                    >
                      {collapsed ? <CaretRight size={14} /> : <CaretDown size={14} />}
                    </button>
                  </span>
                </div>

                {!collapsed && (
                  <div className="space-y-3 p-3">
                    {classes.map((m, ci) => renderClass(m, `${si + 1}.${ci + 1}`))}
                    {classes.length === 0 && (
                      <p className="px-2 py-3 text-sm text-slate-400">No classes yet — add the first one.</p>
                    )}

                    {project ? (
                      <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-dashed border-brand-teal/50 bg-brand-teal/5 px-4 py-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-teal text-white">
                          <Target size={18} weight="bold" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-bold text-brand-navy">{project.title}</div>
                          <div className="text-xs text-slate-500">
                            {project.points} pts · shown at the end of this section · submissions go to the Grading queue
                          </div>
                        </div>
                        <Button variant="secondary" icon={Pencil} onClick={() => setLabModal({ kind: 'project', sectionId: sec.id, lab: project })}>
                          Edit
                        </Button>
                        <button
                          className="btn-ghost p-2 text-rose-600"
                          aria-label="Delete project"
                          onClick={() => {
                            if (confirm(`Delete project "${project.title}" and its submissions?`))
                              void run(() => api.admin.deleteLab(project.id));
                          }}
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ) : (
                      <button
                        className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-semibold text-slate-500 transition hover:border-brand-teal/60 hover:text-brand-teal"
                        onClick={() => setLabModal({ kind: 'project', sectionId: sec.id })}
                      >
                        <Target size={16} weight="bold" /> Add the section project (after the big topic)
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {picked === 'unfiled' && unfiled.length > 0 && (
            <div className="overflow-hidden rounded-2xl border border-dashed border-amber-300 bg-amber-50/40">
              <div className="px-4 py-3 text-sm font-semibold text-amber-800">
                Unfiled classes — edit each one to file it into a section
              </div>
              <div className="space-y-3 p-3">{unfiled.map((m, i) => renderClass(m, `·${i + 1}`))}</div>
            </div>
          )}
        </div>
      )}

      {sectionModal && (
        <Modal open onClose={() => setSectionModal(null)} title={sectionModal.section ? 'Edit section' : 'Add section'}>
          <SectionForm
            key={sectionModal.section?.id ?? 'new'}
            initial={sectionModal.section}
            busy={busy}
            onCancel={() => setSectionModal(null)}
            onSubmit={(v) =>
              run(
                () =>
                  sectionModal.section
                    ? api.admin.updateSection(sectionModal.section.id, v)
                    : api.admin.createSection({ title: v.title, courseId: picked === 'unfiled' ? null : picked }),
                () => setSectionModal(null),
              )
            }
          />
        </Modal>
      )}

      {classModal && (
        <Modal open onClose={() => setClassModal(null)} title={classModal.row ? 'Edit class' : 'Add class'}>
          <ClassForm
            key={classModal.row?.id ?? 'new'}
            initial={classModal.row}
            sections={sections}
            defaultSectionId={classModal.sectionId}
            busy={busy}
            onCancel={() => setClassModal(null)}
            onSubmit={(values) =>
              run(
                () =>
                  classModal.row
                    ? api.admin.updateModule(classModal.row.id, values)
                    : api.admin.createModule(values),
                () => setClassModal(null),
              )
            }
          />
        </Modal>
      )}

      {materialModal && (
        <Modal
          open
          onClose={() => setMaterialModal(null)}
          title={`${materialModal.existing ? 'Edit' : 'Add'} ${MATERIAL_TITLES[materialModal.type].toLowerCase()}`}
        >
          <MaterialForm
            key={`${materialModal.classId}-${materialModal.type}`}
            classId={materialModal.classId}
            type={materialModal.type}
            existing={materialModal.existing}
            busy={busy}
            onCancel={() => setMaterialModal(null)}
            onSubmit={(body) =>
              run(() => api.admin.setMaterial(materialModal.classId, materialModal.type, body), () => setMaterialModal(null))
            }
          />
        </Modal>
      )}

      {labModal && (
        <Modal
          open
          onClose={() => setLabModal(null)}
          title={`${labModal.lab ? 'Edit' : 'Add'} ${
            labModal.kind === 'project' ? 'section project' : labModal.kind === 'exercise' ? 'class exercise' : 'class lab'
          }`}
        >
          <LabForm
            key={labModal.lab?.id ?? 'new'}
            initial={labModal.lab}
            kind={labModal.kind}
            busy={busy}
            onCancel={() => setLabModal(null)}
            onSubmit={(values) =>
              run(
                () =>
                  labModal.lab
                    ? api.admin.updateLab(labModal.lab.id, values)
                    : api.admin.createLab({
                        ...values,
                        kind: labModal.kind,
                        moduleId: labModal.kind === 'project' ? null : labModal.moduleId,
                        sectionId: labModal.kind === 'project' ? labModal.sectionId : null,
                      }),
                () => setLabModal(null),
              )
            }
          />
        </Modal>
      )}
    </div>
  );
}

// ── Expanded class: Materials | Lab | Knowledge check tabs ───────────────────
function ClassTabs({
  row,
  refreshKey,
  onEditMaterial,
  onRemoveMaterial,
  onEditLab,
  onDeleteLab,
}: {
  row: ContentRow;
  refreshKey: number;
  onEditMaterial: (type: LessonType, existing?: Lesson) => void;
  onRemoveMaterial: (type: LessonType) => void;
  onEditLab: (kind: 'lab' | 'exercise', lab?: Lab) => void;
  onDeleteLab: (lab: Lab) => void;
}) {
  const [tab, setTab] = useState<'materials' | 'lab' | 'quiz' | 'practice' | 'feedback'>('materials');
  const state = useAsync(() => api.moduleLessons(row.id), [row.id, refreshKey]);
  const lessons = state.data?.lessons ?? [];
  const labs = state.data?.labs ?? [];
  const byType = new Map(lessons.map((l) => [l.type, l]));
  const added = MATERIAL_TYPES.filter((t) => byType.has(t)).length;

  const tabBtn = (id: typeof tab, label: string, IconCmp: Icon, count: string) => (
    <button
      role="tab"
      aria-selected={tab === id}
      onClick={() => setTab(id)}
      className={cx(
        '-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition',
        tab === id ? 'border-brand-teal text-brand-teal' : 'border-transparent text-slate-500 hover:text-slate-800',
      )}
    >
      <IconCmp size={16} />
      {label}
      <span
        className={cx(
          'rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums',
          tab === id ? 'bg-brand-teal/15 text-brand-teal' : 'bg-slate-100 text-slate-500',
        )}
      >
        {count}
      </span>
    </button>
  );

  return (
    <>
      <div className="flex gap-1 overflow-x-auto border-t border-slate-100 bg-slate-50/70 px-4" role="tablist">
        {tabBtn('materials', 'Class materials', VideoCamera, `${added}/${MATERIAL_TYPES.length}`)}
        {tabBtn('lab', 'Labs', Flask, String(labs.length))}
        {tabBtn('quiz', 'Knowledge check', QuizIcon, '·')}
        {tabBtn('practice', 'Practice Q&A', Exam, '·')}
        {tabBtn('feedback', 'Feedback', Star, '·')}
      </div>

      {state.loading ? (
        <p className="px-4 py-4 text-sm text-slate-400">Loading…</p>
      ) : state.error ? (
        <p className="px-4 py-4 text-sm text-rose-600">{state.error}</p>
      ) : (
        <>
          {tab === 'materials' && (
            <div className="divide-y divide-slate-100 border-t border-slate-100 px-4 py-1">
              {MATERIAL_SLOTS.map((type) => {
                const material = byType.get(type);
                const optional = SUPPLEMENT_TYPES.includes(type);
                const IconCmp = MATERIAL_ICON[type];
                const detail = material
                  ? material.textContent
                    ? `${material.textContent.slice(0, 80)}${material.textContent.length > 80 ? '…' : ''}`
                    : material.streamUrl
                  : MATERIAL_HINT[type];
                return (
                  <div key={type} className="flex items-center gap-3 py-3">
                    <span
                      className={cx(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        material ? 'bg-brand-teal/10 text-brand-teal' : 'bg-slate-100 text-slate-400',
                      )}
                    >
                      <IconCmp size={18} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                        {MATERIAL_TITLES[type]}
                        {type === 'recording' && material && material.durationSec > 0 && (
                          <span className="text-xs font-normal text-slate-400">{formatDuration(material.durationSec)}</span>
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-400">{detail}</div>
                    </div>
                    <Pill tone={material ? 'green' : 'slate'}>
                      {material ? 'Added' : optional ? 'Optional' : 'Missing'}
                    </Pill>
                    {material ? (
                      <>
                        <Button variant="secondary" icon={Pencil} onClick={() => onEditMaterial(type, material)}>
                          Edit
                        </Button>
                        <button
                          className="btn-ghost p-1.5 text-rose-600"
                          aria-label={`Remove ${MATERIAL_TITLES[type]}`}
                          onClick={() => onRemoveMaterial(type)}
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    ) : (
                      <Button icon={Plus} onClick={() => onEditMaterial(type)}>
                        Add
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {tab === 'lab' && (
            <div className="space-y-3 border-t border-slate-100 px-4 py-4">
              {labs.map((lab) => (
                <div
                  key={lab.id}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-teal/10 text-brand-teal">
                    {lab.kind === 'exercise' ? <PuzzlePiece size={18} /> : <Flask size={18} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-700">{lab.title}</span>
                      {lab.kind === 'exercise' && <Pill tone="violet">Exercise</Pill>}
                    </div>
                    <div className="text-xs text-slate-400">
                      {lab.points} pts{lab.env ? ` · ${lab.env}` : ''} ·{' '}
                      {lab.kind === 'exercise' ? 'self-check (question + reveal answer)' : 'submissions land in the Grading queue'}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    icon={Pencil}
                    onClick={() => onEditLab(lab.kind === 'exercise' ? 'exercise' : 'lab', lab)}
                  >
                    Edit
                  </Button>
                  <button className="btn-ghost p-1.5 text-rose-600" aria-label="Delete" onClick={() => onDeleteLab(lab)}>
                    <Trash size={16} />
                  </button>
                </div>
              ))}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                <p className="text-sm text-slate-500">
                  {labs.length === 0 ? 'No labs or exercises for this class yet.' : 'Add another for this class.'}
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" icon={Plus} onClick={() => onEditLab('exercise')}>
                    Add exercise
                  </Button>
                  <Button icon={Plus} onClick={() => onEditLab('lab')}>
                    Add lab
                  </Button>
                </div>
              </div>
            </div>
          )}

          {tab === 'quiz' && <QuizManager moduleId={row.id} moduleTitle={row.title} />}

          {tab === 'practice' && <StudyManager moduleId={row.id} />}

          {tab === 'feedback' && <ModuleFeedbackView moduleId={row.id} />}
        </>
      )}
    </>
  );
}

// Instructor view of the star-rating feedback students left on a class.
function ModuleFeedbackView({ moduleId }: { moduleId: string }) {
  const state = useAsync(() => api.admin.moduleFeedback(moduleId), [moduleId]);
  if (state.loading) return <p className="px-4 py-4 text-sm text-slate-400">Loading feedback…</p>;
  if (state.error) return <p className="px-4 py-4 text-sm text-rose-600">{state.error}</p>;
  const { feedback, count, average } = state.data ?? { feedback: [], count: 0, average: 0 };

  return (
    <div className="space-y-4 border-t border-slate-100 px-4 py-4">
      {count === 0 ? (
        <p className="text-sm text-slate-500">No feedback yet — students can rate this class from the class page.</p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star
                  key={n}
                  size={20}
                  weight={n <= Math.round(average) ? 'fill' : 'regular'}
                  className={n <= Math.round(average) ? 'text-amber-400' : 'text-slate-300'}
                />
              ))}
            </div>
            <span className="text-sm font-bold text-brand-navy">{average.toFixed(1)}</span>
            <span className="text-sm text-slate-400">
              · {count} rating{count > 1 ? 's' : ''}
            </span>
          </div>
          <ul className="space-y-2">
            {feedback
              .filter((f) => f.comment)
              .map((f) => (
                <li key={f.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-brand-navy">{f.userName ?? 'Student'}</span>
                    <span className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={13}
                          weight={n <= f.rating ? 'fill' : 'regular'}
                          className={n <= f.rating ? 'text-amber-400' : 'text-slate-300'}
                        />
                      ))}
                    </span>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{f.comment}</p>
                </li>
              ))}
          </ul>
        </>
      )}
    </div>
  );
}

// ── Forms ────────────────────────────────────────────────────────────────────
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="label-mono mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function SectionForm({
  initial,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: Section;
  busy: boolean;
  onSubmit: (v: { title: string; interviewQuestions: InterviewQuestion[] }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [questions, setQuestions] = useState<InterviewQuestion[]>(initial?.interviewQuestions ?? []);
  const [genBusy, setGenBusy] = useState(false);
  const [answerBusy, setAnswerBusy] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const setQ = (i: number, patch: Partial<InterviewQuestion>) =>
    setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  async function generateQuestions() {
    if (!initial) return;
    setGenBusy(true);
    setGenError(null);
    try {
      const r = await api.admin.generateInterviewQuestions(initial.id);
      setQuestions((qs) => [...qs, ...r.questions]);
    } catch (e) {
      setGenError(e instanceof Error ? e.message : 'Could not generate questions');
    } finally {
      setGenBusy(false);
    }
  }

  async function generateAnswers() {
    if (!initial) return;
    setAnswerBusy(true);
    setGenError(null);
    try {
      const r = await api.admin.generateInterviewAnswers(initial.id, questions);
      setQuestions(r.questions);
    } catch (e) {
      setGenError(e instanceof Error ? e.message : 'Could not generate answers');
    } finally {
      setAnswerBusy(false);
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: title.trim(),
          interviewQuestions: questions
            .map((q) => ({ question: q.question.trim(), howAsked: q.howAsked.trim(), answer: q.answer?.trim() || undefined }))
            .filter((q) => q.question),
        });
      }}
    >
      <Field label="Section title (big topic)">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Networking Fundamentals" required />
      </Field>

      {initial ? (
        <div className="space-y-2 rounded-lg border border-slate-200 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-brand-navy">Interview questions</p>
              <p className="text-xs text-slate-500">
                Shown to students at the end of this section — question, how it’s asked, and a model answer.
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              <Button type="button" variant="secondary" icon={Sparkle} loading={genBusy} onClick={generateQuestions}>
                Generate questions
              </Button>
              <Button
                type="button"
                variant="secondary"
                icon={Sparkle}
                loading={answerBusy}
                onClick={generateAnswers}
                disabled={questions.every((q) => !q.question.trim())}
              >
                Generate answers
              </Button>
            </div>
          </div>
          {genError && <p className="text-xs font-medium text-rose-600">{genError}</p>}
          {questions.map((q, i) => (
            <div key={i} className="space-y-1.5 rounded-lg bg-slate-50 p-2.5">
              <div className="flex items-start gap-2">
                <textarea
                  className="input flex-1"
                  rows={2}
                  value={q.question}
                  placeholder="Question — e.g. “Walk me through triaging a critical vulnerability.”"
                  onChange={(e) => setQ(i, { question: e.target.value })}
                />
                <button
                  type="button"
                  className="btn-ghost p-1.5 text-slate-400 hover:text-rose-600"
                  aria-label="Remove question"
                  onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))}
                >
                  <Trash size={16} />
                </button>
              </div>
              <textarea
                className="input w-full"
                rows={2}
                value={q.howAsked}
                placeholder="How it’s asked / what they’re probing for…"
                onChange={(e) => setQ(i, { howAsked: e.target.value })}
              />
              <textarea
                className="input w-full bg-white"
                rows={3}
                value={q.answer ?? ''}
                placeholder="Model answer (students can reveal this) — write it or use “Generate answers”…"
                onChange={(e) => setQ(i, { answer: e.target.value })}
              />
            </div>
          ))}
          <button
            type="button"
            className="text-xs font-semibold text-brand-teal hover:underline"
            onClick={() => setQuestions((qs) => [...qs, { question: '', howAsked: '' }])}
          >
            + Add interview question
          </button>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Save the section first, then edit it to add interview questions.</p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!title.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
}

function ClassForm({
  initial,
  sections,
  defaultSectionId,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: ContentRow;
  sections: Section[];
  defaultSectionId: string | null;
  busy: boolean;
  onSubmit: (v: {
    title: string;
    topics: string[];
    learningGoals: string[];
    status: ModuleStatus;
    sectionId: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [topics, setTopics] = useState(initial?.topics.join(', ') ?? '');
  const [status, setStatus] = useState<ModuleStatus>(initial?.status ?? 'planned');
  const [sectionId, setSectionId] = useState<string>(initial?.sectionId ?? defaultSectionId ?? '');
  const [goalsText, setGoalsText] = useState(initial?.learningGoals?.join('\n') ?? '');

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: title.trim(),
          topics: topics
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
          learningGoals: goalsText.split('\n').map((g) => g.trim()).filter(Boolean),
          status,
          sectionId: sectionId || null,
        });
      }}
    >
      <Field label="Class title (sub-topic)">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </Field>
      <Field label="Topics (comma-separated)">
        <input className="input" value={topics} onChange={(e) => setTopics(e.target.value)} placeholder="RBAC, NSGs, Storage" />
      </Field>
      <Field label="Learning goals — optional (“by the end you'll be able to…”)">
        <textarea
          className="input"
          rows={4}
          value={goalsText}
          onChange={(e) => setGoalsText(e.target.value)}
          placeholder={'One goal per line, e.g.\nExplain the difference between Entra roles and Azure RBAC\nRegister an app and grant admin consent'}
        />
        <p className="mt-1 text-xs text-slate-400">One goal per line — each line becomes its own checklist item.</p>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Section">
          <select className="input" value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
            <option value="">— Unfiled —</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as ModuleStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <p className="text-xs text-slate-400">Classes appear to learners in list order — drag to reorder after saving.</p>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!title.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
}

function MaterialForm({
  classId,
  type,
  existing,
  busy,
  onSubmit,
  onCancel,
}: {
  classId: string;
  type: LessonType;
  existing?: Lesson;
  busy: boolean;
  onSubmit: (v: {
    streamUrl?: string | null;
    textContent?: string | null;
    durationSec?: number;
    chapters?: Chapter[] | null;
  }) => void;
  onCancel: () => void;
}) {
  const textFirst = type === 'summary' || type === 'transcript' || type === 'notes';
  const [mode, setMode] = useState<'file' | 'text'>(existing?.textContent ? 'text' : textFirst && !existing?.streamUrl ? 'text' : 'file');
  const [url, setUrl] = useState(existing?.streamUrl ?? '');
  const [text, setText] = useState(existing?.textContent ?? '');
  const [minutes, setMinutes] = useState(existing && existing.durationSec > 0 ? String(Math.round(existing.durationSec / 60)) : '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Recording chapters (edited as {time, title} strings; parsed on save).
  const [chapterRows, setChapterRows] = useState<{ time: string; title: string }[]>(
    () => (existing?.chapters ?? []).map((c) => ({ time: fmtTime(c.startSec), title: c.title })),
  );
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  async function generateChapters() {
    setGenBusy(true);
    setGenError(null);
    try {
      const chapters = await api.admin.generateChapters(classId);
      setChapterRows(chapters.map((c) => ({ time: fmtTime(c.startSec), title: c.title })));
      if (chapters.length === 0) setGenError('No chapters were produced — check the transcript.');
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Could not generate chapters');
    } finally {
      setGenBusy(false);
    }
  }

  function chaptersFromRows(): Chapter[] {
    return chapterRows
      .map((r) => ({ startSec: parseTime(r.time), title: r.title.trim() }))
      .filter((c) => c.title && Number.isFinite(c.startSec))
      .sort((a, b) => a.startSec - b.startSec);
  }

  // Transcript: pull the .docx Teams saved next to the SharePoint recording.
  const [fetchBusy, setFetchBusy] = useState(false);
  const [fetchNote, setFetchNote] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  async function fetchTranscript() {
    setFetchBusy(true);
    setFetchNote(null);
    setFetchError(null);
    try {
      const r = await api.admin.fetchTranscript(classId);
      setText(r.text);
      setFetchNote(`Pulled from “${r.sourceName}” — review and Save.`);
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Could not fetch the transcript');
    } finally {
      setFetchBusy(false);
    }
  }

  async function generateRecap() {
    setFetchBusy(true);
    setFetchNote(null);
    setFetchError(null);
    try {
      const r = await api.admin.generateSummary(classId);
      setText(r.text);
      setFetchNote('Recap drafted from the transcript — review and Save.');
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Could not generate the recap');
    } finally {
      setFetchBusy(false);
    }
  }

  async function generateNotes() {
    setFetchBusy(true);
    setFetchNote(null);
    setFetchError(null);
    try {
      const r = await api.admin.generateNotes(classId);
      setText(r.text);
      setFetchNote('Notes drafted from the transcript — review and Save.');
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Could not generate the notes');
    } finally {
      setFetchBusy(false);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const contentType = file.type || 'application/octet-stream';
      const { uploadUrl, fileUrl } = await api.admin.uploadContentSas(file.name, contentType);
      if (uploadUrl) {
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType },
          body: file,
        });
        if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      }
      setUrl(fileUrl);
      setMode('file');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  const canSave = mode === 'text' ? text.trim().length > 0 : url.trim().length > 0;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(
          mode === 'text'
            ? { textContent: text.trim(), streamUrl: null }
            : {
                streamUrl: extractEmbedSrc(url),
                textContent: null,
                ...(type === 'recording'
                  ? {
                      durationSec: Math.max(0, Math.round((Number(minutes) || 0) * 60)),
                      chapters: chaptersFromRows(),
                    }
                  : {}),
              },
        );
      }}
    >
      <p className="text-xs text-slate-500">{MATERIAL_HINT[type]}</p>

      {(type === 'transcript' || type === 'summary' || type === 'notes' || type === 'incident') && (
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 text-sm font-semibold">
          <button
            type="button"
            className={cx('flex-1 rounded-lg px-3 py-1.5 transition', mode === 'text' ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500')}
            onClick={() => setMode('text')}
          >
            Write text
          </button>
          <button
            type="button"
            className={cx('flex-1 rounded-lg px-3 py-1.5 transition', mode === 'file' ? 'bg-white text-brand-navy shadow-sm' : 'text-slate-500')}
            onClick={() => setMode('file')}
          >
            File / link
          </button>
        </div>
      )}

      {mode === 'text' ? (
        <div className="space-y-2">
          {(type === 'transcript' || type === 'summary' || type === 'notes') && (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 text-xs text-slate-500">
                  {type === 'transcript'
                    ? 'Pull it automatically from the recording’s SharePoint folder.'
                    : type === 'notes'
                      ? 'Draft study notes automatically from the class transcript.'
                      : 'Draft it automatically from the class transcript.'}
                </p>
                {type === 'transcript' ? (
                  <Button type="button" variant="secondary" icon={CloudArrowDown} loading={fetchBusy} onClick={fetchTranscript}>
                    Fetch from SharePoint
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    icon={Sparkle}
                    loading={fetchBusy}
                    onClick={type === 'notes' ? generateNotes : generateRecap}
                  >
                    Generate from transcript
                  </Button>
                )}
              </div>
              {fetchNote && <p className="text-xs font-medium text-emerald-600">{fetchNote}</p>}
              {fetchError && <p className="text-xs font-medium text-rose-600">{fetchError}</p>}
            </>
          )}
          <Field label={MATERIAL_TITLES[type]}>
            <textarea className="input" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste or write the text here…" />
          </Field>
        </div>
      ) : (
        <>
          <Field label={type === 'recording' ? 'Embed URL (YouTube / Vimeo / Stream) or uploaded file' : 'File or embed URL'}>
            <input className="input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… (or upload below)" />
          </Field>
          {type === 'recording' && (
            <Field label="Duration (minutes)">
              <input className="input" type="number" min={0} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </Field>
          )}
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-3">
            <div className="flex items-center justify-between gap-3">
              <p className="min-w-0 text-xs text-slate-500">
                Uploads go to private storage; learners get a time-limited link.
              </p>
              <label className={cx('btn-secondary shrink-0 cursor-pointer', uploading && 'pointer-events-none opacity-60')}>
                {uploading ? 'Uploading…' : 'Choose file'}
                <input type="file" className="hidden" disabled={uploading} onChange={handleFile} />
              </label>
            </div>
            {uploadError && <p className="mt-2 text-xs font-medium text-rose-600">{uploadError}</p>}
            {!uploadError && !uploading && url && (
              <p className="mt-2 text-xs font-medium text-emerald-600">
                ✓ File ready — click <strong>Save</strong> to attach it. <span className="break-all font-normal text-emerald-700/70">{url}</span>
              </p>
            )}
          </div>

          {type === 'recording' && (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-brand-navy">Chapters</p>
                  <p className="text-xs text-slate-500">Jump-points shown next to the video. Generate from the transcript, then edit.</p>
                </div>
                <Button type="button" variant="secondary" icon={Sparkle} loading={genBusy} onClick={generateChapters}>
                  Generate from transcript
                </Button>
              </div>
              {genError && <p className="text-xs font-medium text-rose-600">{genError}</p>}
              {chapterRows.length > 0 && (
                <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                  {chapterRows.map((row, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className="input w-20 text-center tabular-nums"
                        value={row.time}
                        placeholder="0:00"
                        aria-label="Timestamp"
                        onChange={(e) =>
                          setChapterRows((rows) => rows.map((r, j) => (j === i ? { ...r, time: e.target.value } : r)))
                        }
                      />
                      <input
                        className="input flex-1"
                        value={row.title}
                        placeholder="Chapter title"
                        aria-label="Chapter title"
                        onChange={(e) =>
                          setChapterRows((rows) => rows.map((r, j) => (j === i ? { ...r, title: e.target.value } : r)))
                        }
                      />
                      <button
                        type="button"
                        className="btn-ghost p-1.5 text-slate-400 hover:text-rose-600"
                        aria-label="Remove chapter"
                        onClick={() => setChapterRows((rows) => rows.filter((_, j) => j !== i))}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="text-xs font-semibold text-brand-teal hover:underline"
                onClick={() => setChapterRows((rows) => [...rows, { time: '', title: '' }])}
              >
                + Add chapter
              </button>
            </div>
          )}
        </>
      )}

      <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-3 border-t border-slate-100 bg-white px-6 py-4">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!canSave || uploading}>
          {uploading ? 'Uploading…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function LabForm({
  initial,
  kind,
  busy,
  onSubmit,
  onCancel,
}: {
  initial?: Lab;
  kind: 'lab' | 'project' | 'exercise';
  busy: boolean;
  onSubmit: (v: {
    title: string;
    points: number;
    env: string;
    instructions: string;
    resourceUrl: string | null;
    guideHtml: string | null;
    answerHtml: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const isExercise = kind === 'exercise';
  const [title, setTitle] = useState(initial?.title ?? '');
  const [points, setPoints] = useState(String(initial?.points ?? (kind === 'project' ? 150 : 100)));
  const [env, setEnv] = useState(initial?.env ?? '');
  const [resourceUrl, setResourceUrl] = useState(initial?.resourceUrl ?? '');
  const [guideHtml, setGuideHtml] = useState(initial?.guideHtml ?? '');
  const [answerHtml, setAnswerHtml] = useState(initial?.answerHtml ?? '');
  const [instructions, setInstructions] = useState(initial?.instructions ?? '');

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          title: title.trim(),
          points: Number(points) || 0,
          env: env.trim(),
          instructions: instructions.trim(),
          resourceUrl: resourceUrl.trim() || null,
          guideHtml: guideHtml.trim() || null,
          answerHtml: answerHtml.trim() || null,
        });
      }}
    >
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Points">
          <input className="input" type="number" min={0} value={points} onChange={(e) => setPoints(e.target.value)} />
        </Field>
        <Field label="Environment">
          <input className="input" value={env} onChange={(e) => setEnv(e.target.value)} placeholder="Azure free tier" />
        </Field>
      </div>
      <Field label="Guide link (PDF / SharePoint) — optional">
        <input
          className="input"
          type="url"
          value={resourceUrl}
          onChange={(e) => setResourceUrl(e.target.value)}
          placeholder="https://…sharepoint.com/…/lab-guide.pdf"
        />
        <p className="mt-1 text-xs text-slate-400">
          A PDF renders <strong>inline</strong> on the lab page (page-by-page, like the class slides) — attach the full
          guide here so instructions can stay short. SharePoint links are resolved for students automatically.
        </p>
      </Field>
      <HtmlUploadField
        label={isExercise ? 'Question page (HTML) — optional' : 'Inline HTML guide — optional'}
        value={guideHtml}
        onChange={setGuideHtml}
        placeholder="<h1>…</h1>  — paste or upload a self-contained HTML page"
        hint={
          isExercise
            ? 'The exercise question — renders inline (auto-height, sandboxed) for students.'
            : 'Renders inline on the page (sandboxed) — an alternative to a PDF link.'
        }
      />
      <HtmlUploadField
        label="Answer page (HTML) — optional"
        value={answerHtml}
        onChange={setAnswerHtml}
        placeholder="<h1>Answer…</h1>  — paste or upload the model answer"
        hint="Students get a “Reveal answer” button below the question — ideal for exercises."
      />
      <Field label="Instructions (a short summary + what evidence to submit)">
        <textarea className="input" rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      </Field>
      {kind === 'project' && (
        <p className="text-xs text-slate-400">
          Projects are shown to learners at the end of the section with a "do this after the classes" note.
        </p>
      )}
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={busy} disabled={!title.trim()}>
          Save
        </Button>
      </div>
    </form>
  );
}
